import { toast } from "sonner";

/**
 * Handle AI fetch responses globally.
 *
 * Call this after any AI fetch:
 *   const res = await fetch(...);
 *   if (handleAiResponse(res)) return; // true = blocked, stop processing
 *
 * Dispatches window events that CreditProvider listens to.
 */
export async function handleAiResponse(res) {
  if (res.status === 403) {
    try {
      const body = await res.clone().json();
      if (body?.detail?.error === "AI_CREDITS_EXCEEDED") {
        window.dispatchEvent(new Event("ai-credits-exhausted"));
        toast.error("AI credits exhausted for this cycle.");
        return true;
      }
    } catch { /* ignore parse errors */ }
  }

  if (res.status === 429) {
    try {
      const body = await res.clone().json();
      const error = body?.detail?.error;
      if (error === "DUPLICATE_REQUEST") {
        toast.warning("Duplicate request — please wait a moment before retrying.");
      } else if (error === "RATE_LIMIT_EXCEEDED") {
        const retryAfter = body?.detail?.retry_after_seconds;
        const unit = retryAfter >= 3600 ? "hour" : "minute";
        toast.error(`Rate limit reached. Please wait a ${unit} before trying again.`);
      } else {
        toast.warning(body?.detail?.message || "Too many requests. Please wait.");
      }
      return true;
    } catch { /* ignore */ }
  }

  // Refresh credits counter after successful AI calls
  if (res.ok && res.url?.includes("/api/ai/")) {
    window.dispatchEvent(new Event("ai-credits-changed"));
  }

  return false;
}

/**
 * Unwrap AI response that is wrapped in { result, credits_used, credits_remaining }.
 * Returns the inner `result` if wrapped, or the original data if not.
 *
 * Usage:
 *   const data = unwrapAi(await response.json());
 */
export function unwrapAi(data) {
  if (data && typeof data === "object" && "result" in data && "credits_used" in data) {
    window.dispatchEvent(new Event("ai-credits-changed"));
    return data.result;
  }
  return data;
}
